import React, { useState, useEffect } from "react";
import {
  Drawer,
  Button,
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  Accordion,
  AccordionSummary,
  Card,
  ListItemIcon,
  CircularProgress,
} from "@material-ui/core";
import CloseRedIcon from "../../../components/Icons/CloseRedIcon";
import AvatorIcon from "../../../components/Icons/AvatorIcon";
import { useStyles } from "./style";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import CommunityBulletIcon from "../../../components/Icons/CommunityBulletIcon";
import RightArrowBlueIcon from "../../../components/Icons/RightArrowBlueIcon";
import LeftCarretIcon from "../../../components/Icons/LeftCarrotIcon";
import { useHistory } from "react-router";
import CloseLockIcon from "../../../components/Icons/CloseLockIcon";
import OpenLockIcon from "../../../components/Icons/OpenLockIcon";
import { getUserProgramEnrollmentsApi } from "../../../api";
const ProfileDrawer = ({ open, onClose, settingItems, classes, role = "user" }) => {
  const className = useStyles();
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const { user } = useSelector((state: RootState) => state.auth);
  const { singleProgram } = useSelector((state: RootState) => state.programReducer);
  const history = useHistory();

  const [userEnrollments, setUserEnrollments] = useState<any[]>([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [activeCommunity, setActiveCommunity] = useState<any>(null);

  // Get active enrollment from localStorage
  useEffect(() => {
    const activeEnrollment = JSON.parse(localStorage.getItem("activeProgramEnrollment") || "{}");
    if (activeEnrollment.communityIdentifier && singleProgram) {
      const community = singleProgram.communities?.find(
        (c: any) => c.communityIdentifier === activeEnrollment.communityIdentifier
      );
      setActiveCommunity(community || { communityName: activeEnrollment.communityIdentifier });
    }
  }, [singleProgram]);

  // Fetch all user enrollments
  useEffect(() => {
    if (!user?._id || !open) return;

    setLoadingEnrollments(true);
    getUserProgramEnrollmentsApi(user._id)
      .then((response: any) => {
        // API returns ResultResponse with data property
        const enrollments = Array.isArray(response) ? response : (response?.data || []);
        setUserEnrollments(enrollments);
      })
      .catch((error) => {
        console.error("Error fetching enrollments:", error);
      })
      .finally(() => {
        setLoadingEnrollments(false);
      });
  }, [user?._id, open]);

  const handleExpandToggle = (label: string) => {
    setExpandedItems((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const handleSwitchCommunity = (enrollment: any) => {
    // Update localStorage with new active enrollment
    const activeEnrollment = {
      programId: enrollment.programId,
      userId: enrollment.userId,
      communityIdentifier: enrollment.communityIdentifier,
    };
    localStorage.setItem("activeProgramEnrollment", JSON.stringify(activeEnrollment));

    // Reload the page to apply the new community context
    window.location.reload();
  };

  const renderNestedItems = (nestedItems: any[]) =>
    nestedItems.map((item, index) => (
      <ListItem key={index} onClick={() => item.path && history.push(item.path)}
      >
        <item.icon width={30} height={30} />
        <ListItemText primary={item.label} style={{ marginLeft: "20px", color: "black" }} />
      </ListItem>
    ));

  return (
    <Drawer anchor="left" open={open} onClose={onClose} classes={{ paper: classes.profileDrawerPaper }}>
      <Button onClick={onClose} className={classes.closeProfileDrawerButton}>
        <CloseRedIcon />
      </Button>

      <Box className={className.profileInfo}>
        <AvatorIcon width={100} height={100} />
        <Typography className={className.textHeading}>{user?.name} {user?.lastName}</Typography>
        <Typography>{user?.email}</Typography>

        <Accordion className={className.communities}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
            <Typography className={classes.heading}>
              {activeCommunity?.communityName || "Select Community"}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box style={{ width: "100%" }}>
              {loadingEnrollments ? (
                <Box display="flex" justifyContent="center" padding="20px">
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <>
                  {/* List of communities user can switch to */}
                  {userEnrollments.length > 1 && (
                    <Box style={{ marginBottom: "10px" }}>
                      <Typography style={{ fontSize: "12px", fontWeight: 600, marginBottom: "8px", paddingLeft: "10px" }}>
                        Switch Community:
                      </Typography>
                      {userEnrollments.map((enrollment, index) => {
                        // Get community name from program
                        const community = singleProgram?.communities?.find(
                          (c: any) => c.communityIdentifier === enrollment.communityIdentifier
                        );
                        const communityName = community?.communityName || enrollment.communityIdentifier;
                        const isActive = enrollment.communityIdentifier === activeCommunity?.communityIdentifier;

                        return (
                          <Card
                            key={index}
                            className={className.communitiesAccordianCard}
                            style={{
                              flexDirection: "row",
                              marginBottom: "5px",
                              backgroundColor: isActive ? "#E3F2FD" : "#fff",
                              cursor: isActive ? "default" : "pointer",
                              opacity: isActive ? 0.7 : 1,
                            }}
                            onClick={() => !isActive && handleSwitchCommunity(enrollment)}
                          >
                            <CommunityBulletIcon width={25} height={25} />
                            <Typography style={{ flex: 1 }}>
                              {communityName}
                              {isActive && (
                                <span style={{ fontSize: "11px", color: "#1976d2", marginLeft: "8px" }}>
                                  (Current)
                                </span>
                              )}
                            </Typography>
                            {!isActive && <RightArrowBlueIcon width={20} height={20} />}
                          </Card>
                        );
                      })}
                    </Box>
                  )}

                  {/* Discover communities button */}
                  <Card className={className.communitiesAccordianCard} style={{ flexDirection: "row" }}>
                    <CommunityBulletIcon width={25} height={25} />
                    <Typography>Discover communities</Typography>
                    <Box onClick={() => history.push("/" + role + "/communities/discover")}>
                      <RightArrowBlueIcon width={25} height={25} />
                    </Box>
                  </Card>
                </>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      <List>
        {settingItems.map((text, index) => (
          <ListItem button key={index}>
            {text.nested ? (
              <Accordion
                className={className.menuAccordian}
                onChange={() => handleExpandToggle(text.label)}
                expanded={!!expandedItems[text.label]}
              >
                <AccordionSummary expandIcon={text.label === "SafteyAlert" ? <LeftCarretIcon /> : <ExpandMoreIcon />} className={classes.accordionSummary}>
                  <Box onClick={text.onClick && (() => text.onClick())} >
                    <text.icon width={30} height={30} />
                  </Box>
                  <ListItemText primary={text.label} style={{ marginLeft: "28px" }} />
                  {text.subIcon && (
                    <Box style={{ marginTop: "5px"}}>
                      {expandedItems[text.label] ? <OpenLockIcon /> : <CloseLockIcon />}
                    </Box>
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  <Card className={className.communitiesAccordianCard}>
                    {text.nestedItem && renderNestedItems(text.nestedItem)}
                  </Card>
                </AccordionDetails>
              </Accordion>
            ) : (
              <>
                <ListItemIcon style={{ marginTop: "10px" }}>
                  <text.icon width={30} height={30} />
                </ListItemIcon>
                <ListItemText
                  primary={text.label}
                  className={className.listItemText}
                  primaryTypographyProps={{
                    style: {
                      fontFamily: "MontserratRegular",
                      fontSize: "20px",
                      fontWeight: 400,
                      lineHeight: "20.15px",
                      color: text.className === "danger" ? "red" : "black",
                    },
                  }}
                  onClick={text.onClick}
                />
              </>
            )}
          </ListItem>
        ))}
      </List>
    </Drawer>
  );
};
export default ProfileDrawer;
